import * as Yup from 'yup';
import { FieldData, TextField, TextArea, Tooltip } from 'vopay';
import { useTranslation } from 'react-i18next';

export type HubspotFormValues = {
  hubSpotDealId: string;
  additionalNotes: string;
};

const useHubspotFields = (
  onDealIdBlur: (dealId: string) => void,
  isLoaded: boolean,
  isDealIdDisabled = false,
  existingDealId: string | null = null,
): FieldData<HubspotFormValues>[] => {
  const { t } = useTranslation('translations', { keyPrefix: 'R1AccountCreation.Hubspot' });

  return [
    {
      fieldName: 'hubSpotDealId',
      label: () => t('HubSpotDealId.label', { defaultValue: 'HubSpot Deal ID' }),
      getInputState: () => 'required',
      showField: () => true,
      validate: () => {
        const schema = Yup.string().required();
        return existingDealId
          ? schema.test(
              'deal-not-exists',
              t('HubSpotDealId.alreadyExists', { defaultValue: 'A deal with this ID already exists' }),
              (value) => value !== existingDealId,
            )
          : schema;
      },
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({
          onBlur: () => {
            if (ctx.values.hubSpotDealId) {
              onDealIdBlur(ctx.values.hubSpotDealId);
            }
          },
        });
        return (
          <TextField
            name={fieldData.name}
            variant="outlineShadow"
            value={String(fieldData.value ?? '')}
            onChange={fieldData.onChange}
            onBlur={fieldData.onBlur}
            error={fieldData.errorMsg}
            disabled={isDealIdDisabled || fieldData.disabled}
            placeholder={t('HubSpotDealId.placeholder', { defaultValue: 'Enter HubSpot Deal ID' })}
          />
        );
      },
    },
    {
      fieldName: 'additionalNotes',
      label: () => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          {t('AdditionalNotes.label', { defaultValue: 'Additional Notes' })}
          <Tooltip
            information={t('AdditionalNotes.tooltip', {
              defaultValue: 'Additional notes provide more context to the sales manager reviewing this form.',
            })}
            type="hover"
            variant="outlined"
            icon="info"
            position="top"
            buttonWidth="18px"
            iconWidth="12"
            hasArrow
          />
        </span>
      ),
      getInputState: () => 'optional',
      showField: () => isLoaded,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextArea
            name={fieldData.name}
            variant="outlineShadow"
            value={String(fieldData.value ?? '')}
            onChange={fieldData.onChange}
            onBlur={fieldData.onBlur}
            error={fieldData.errorMsg}
            disabled={fieldData.disabled}
            placeholder={t('AdditionalNotes.placeholder', {
              defaultValue: 'Enter any additional notes for the sales manager...',
            })}
            style={{ minHeight: '120px', height: '100%' }}
          />
        );
      },
    },
  ];
};

export default useHubspotFields;
